import React, { useCallback, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import { useResizableColumns } from './useResizableColumns';
import { getCustomStyles, LoadingComponent } from './tableStyles';

const ReusableDataTable = ({
    data,
    columns,
    loading,
    pagination,
    onChangePage,
    onChangeRowsPerPage,
    onSort,
    selectableRows = false,
    selectedIds = [],
    onSelectedRowsChange,
    onClickedRow,
    clickedRowHighlight = false,
    /** When true, only columns with resizable: true get a drag handle. */
    enableResize = false,
}) => {
    const [clickedRowId, setClickedRowId] = useState(null);

    const { enhancedColumns, handleSort } = useResizableColumns(columns, onSort, enableResize);

    const handlePageChange = useCallback((current_page) => {
        onChangePage(current_page);
    }, [onChangePage]);

    const handlePerRowsChange = useCallback((newPerPage, page) => {
        onChangeRowsPerPage(newPerPage, page);
    }, [onChangeRowsPerPage]);

    const handleSelectedRowsChange = useCallback((state) => {
        onSelectedRowsChange(state);
    }, [onSelectedRowsChange]);

    const isRowSelected = useCallback((row) => {
        return selectedIds.includes(row.id);
    }, [selectedIds]);

    const hasClickHandler = useMemo(() => {
        return onClickedRow && typeof onClickedRow === 'function' && onClickedRow.toString() !== '() => {}';
    }, [onClickedRow]);

    const handleRowClicked = useCallback((row, event) => {
        if (clickedRowHighlight) {
            setClickedRowId(row.id);
        }

        onClickedRow(row, event);
    }, [clickedRowHighlight, onClickedRow]);

    const conditionalRowStyles = useMemo(() => {
        if (!clickedRowHighlight) {
            return [];
        }

        return [{
            when: row => row.id === clickedRowId,
            style: {
                backgroundColor: '#eef4ff',
            },
        }];
    }, [clickedRowHighlight, clickedRowId]);

    const customStyles = useMemo(
        () => getCustomStyles(hasClickHandler, enableResize),
        [hasClickHandler, enableResize]
    );

    return (
        <DataTable
            columns={enhancedColumns}
            data={data}
            progressPending={loading}
            progressComponent={LoadingComponent}
            pagination
            paginationServer
            paginationTotalRows={pagination?.total || 0}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
            selectableRowsVisibleOnly
            selectableRowsNoSelectAll
            selectableRows={selectableRows}
            selectableRowSelected={isRowSelected}
            selectableRowsHighlight
            onSelectedRowsChange={handleSelectedRowsChange}
            clearSelectedRows
            onSort={handleSort}
            {...(hasClickHandler && { onRowClicked: handleRowClicked })}
            conditionalRowStyles={conditionalRowStyles}
            paginationPerPage={pagination?.per_page || 10}
            customStyles={customStyles}
            className="scrollable-table-content"
            responsive
            fixedHeader
            fixedHeaderScrollHeight="60vh"
            persistTableHead
            paginationDefaultPage={pagination?.current_page || 1}
        />
    );
};

export default ReusableDataTable;
